import CardCommon, { CardCommonType } from '@op/components/CardCommon';
import { DBO_V } from '@op/utils/format';
import CountryItem from '@opm/pages/Hashtag/components/CountryItem';
import React, { useMemo } from 'react';
import { Progress } from 'tea-component';
import useIntls from '@opm/hooks/useIntls';
import './index.scss';

const defaultColorMap = ['#5086F3', '#5EC8F1', '#69D19C', '#F8D74D', '#7F6FF1'];

interface IDistributionProgress extends CardCommonType {
  data: Array<{
    [key: string]: number | string | undefined;
  }>;
  valueKey: string;
  title: string;
  labelKey?: string;
  /** Render label as country flag + name */
  isCountry?: boolean;
  /** Custom label renderer — overrides default text / country rendering */
  renderLabel?: (label: string) => React.ReactNode;
  classNames?: string;
  colorMap?: string[];
  topCount?: number;
}

export default function DistributionProgress(props: IDistributionProgress) {
  const {
    data,
    valueKey,
    title,
    labelKey = 'language',
    isCountry,
    renderLabel,
    classNames,
    colorMap = defaultColorMap,
    topCount = 5,
    ...cardProps
  } = props;
  const { intl } = useIntls();

  const formatData = useMemo(() => {
    const dataList = data || [];
    const sortedList = [...dataList]
      .filter(
        (item) =>
          Number(item?.[valueKey]) > 0 && item?.[labelKey] !== 'all',
      )
      .sort(
        (a, b) => Number(b?.[valueKey] || 0) - Number(a?.[valueKey] || 0),
      );
    const topList = sortedList.slice(0, topCount);
    const maxValue = Number(topList[0]?.[valueKey]) || 0;
    return topList.map((val, index) => {
      const label = String(val?.[labelKey] || '');
      const value = Number(val?.[valueKey]) || 0;
      const color = colorMap[index];
      const percent =
        maxValue > 0 ? +((value / maxValue) * 100).toFixed(2) : 0;
      return { label, value, percent, color };
    });
  }, [data, valueKey, labelKey]);

  return (
    <CardCommon
      title={intl(title)}
      {...cardProps}
      className={`dbom-meme-distribution-progress ${classNames || ''}`}
      empty={formatData?.length === 0}
      emptyHeight={130}
      hasEmptyImg={false}
      contentCard
    >
      <div className="dbom-meme-distribution-progress-content">
        {formatData?.map((val) => (
          <DistributionProgressItem
            key={val.label}
            isCountry={isCountry}
            renderLabel={renderLabel}
            {...val}
          />
        ))}
      </div>
    </CardCommon>
  );
}

const DistributionProgressItem = (props: {
  label: string;
  value: number;
  color: string;
  percent: number;
  isCountry?: boolean;
  renderLabel?: (label: string) => React.ReactNode;
}) => {
  const { label, value, color, percent, isCountry, renderLabel } = props;

  const labelNode = renderLabel
    ? renderLabel(label)
    : isCountry
      ? <CountryItem country={label} />
      : <span className="dbom-meme-distribution-label">{label}</span>;

  return (
    <div className="dbom-meme-distribution-progress-item">
      <div className="distribution-item-left">
        {labelNode}
      </div>
      <Progress
        percent={percent}
        strokeColor={color}
        className="dbom-meme-distribution-progress-item-progress"
      />
      <span className="dbom-meme-distribution-progress-item-value">
        {DBO_V.reviews(value)}
      </span>
    </div>
  );
};
